import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.jsx';

/* Import-flow primitives shared by CalendarImport + JiraImport.
   Both importers stage rows, validate each row, and commit. These let
   them share the same UX:
     - rowImportStatus(...)  pure: derives a row's status from its draft
     - splitActionCase(...)  pure: which of the four footer cases applies
     - RowStatusChip         renders the status as a chip in the row header
     - ImportSplitAction     primary button with a split action when some
                             rows are ready and some aren't                  */

export function rowImportStatus(draft, opts = {}) {
  if (opts.alreadyLogged) return 'logged';
  if (!draft || !draft.included) return 'excluded';
  const needsAct = !draft.activity;
  const needsProd = !!opts.requiresProduct && !draft.product;
  if (needsAct && needsProd) return 'needs-both';
  if (needsAct) return 'needs-activity';
  if (needsProd) return 'needs-product';
  return 'ready';
}

export function splitActionCase(readyCount, incompleteCount) {
  if (readyCount === 0 && incompleteCount === 0) return 'none';
  if (readyCount === 0) return 'fix';
  if (incompleteCount === 0) return 'all';
  return 'split';
}

export function RowStatusChip({ status, duration }) {
  const cfg = {
    ready: { tone: 'mint', label: duration ? `Ready · ${duration}` : 'Ready' },
    'needs-activity': { tone: 'apricot', label: 'Pick activity', arrow: true },
    'needs-product': { tone: 'apricot', label: 'Pick product', arrow: true },
    'needs-both': { tone: 'apricot', label: 'Pick activity + product', arrow: true },
  }[status];
  if (!cfg) return null;
  const isOk = cfg.tone === 'mint';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        fontWeight: 700,
        padding: '3px 9px',
        borderRadius: 999,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        flex: 'none',
        background: isOk ? 'var(--sp-mint-100)' : 'var(--sp-apricot-100)',
        color: isOk ? '#007C7C' : 'var(--sp-apricot-ink)',
      }}
    >
      {isOk ? (
        <Icon name="check" size={10} stroke={2.6} />
      ) : (
        <span
          style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }}
        />
      )}
      {cfg.label}
      {cfg.arrow && <span style={{ opacity: 0.85, marginLeft: 2 }}>→</span>}
    </span>
  );
}

export function ImportSplitAction({
  readyCount,
  incompleteCount,
  tone = 'mint',
  noun = 'entry',
  nounPlural = 'entries',
  onImport,
  onImportAndSkip,
  onJumpToBlocker,
  disabled = false,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const accent = tone === 'jira' ? '#0052CC' : '#191919';
  const which = splitActionCase(readyCount, incompleteCount);

  if (which === 'none') {
    return (
      <button
        className="btn btn-primary"
        disabled
        style={{ whiteSpace: 'nowrap' }}
      >
        Import
      </button>
    );
  }

  if (which === 'fix') {
    return (
      <button
        className="btn btn-secondary"
        onClick={onJumpToBlocker}
        disabled={disabled}
        style={{ whiteSpace: 'nowrap' }}
      >
        Fix {incompleteCount} to import
      </button>
    );
  }

  if (which === 'all') {
    return (
      <button
        className="btn btn-primary"
        onClick={onImport}
        disabled={disabled}
        style={{ background: accent, whiteSpace: 'nowrap' }}
      >
        <Icon name="check" size={14} stroke={2.4} />
        &nbsp;Import {readyCount} {readyCount === 1 ? noun : nounPlural}
      </button>
    );
  }

  // 'split' — mixed
  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className="btn btn-primary"
        onClick={onImport}
        disabled={disabled}
        style={{
          background: accent,
          whiteSpace: 'nowrap',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        <Icon name="check" size={14} stroke={2.4} />
        &nbsp;Import {readyCount} ready
      </button>
      <button
        className="btn btn-primary"
        onClick={() => setMenuOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title="More options"
        style={{
          background: accent,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderLeft: '1px solid rgba(255,255,255,0.18)',
          padding: '0 10px',
        }}
      >
        <Icon name="chevD" size={14} />
      </button>
      {menuOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            right: 0,
            background: '#fff',
            border: '1px solid var(--sp-border)',
            borderRadius: 12,
            boxShadow: 'var(--sp-shadow-md)',
            padding: 6,
            width: 300,
            zIndex: 100,
          }}
        >
          <button
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              if (onImportAndSkip) onImportAndSkip();
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '10px 12px',
              borderRadius: 8,
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-fg-1)' }}>
              Import {readyCount} ready, skip {incompleteCount}
            </div>
            <div style={{ fontSize: 12, color: 'var(--sp-fg-3)', marginTop: 2 }}>
              Skipped {nounPlural} stay on this day for later.
            </div>
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              if (onJumpToBlocker) onJumpToBlocker();
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '10px 12px',
              borderRadius: 8,
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-fg-1)' }}>
              Wait — let me fix the rest first
            </div>
            <div style={{ fontSize: 12, color: 'var(--sp-fg-3)', marginTop: 2 }}>
              Jumps to the next {noun} missing info.
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
